import { Trans, useLingui } from '@lingui/react/macro';
import { isBefore, isToday, setHours } from 'date-fns';
import SimpleBar from 'simplebar-react';

import { Activity, ActivityOfType, stackSimilarActivities } from '@/components/activities/Activity';
import { ArticleActionRefusedByClientActivity } from '@/components/activities/ArticleActionRefusedByClientActivity';
import { ArticleAddedToShipmentActivity } from '@/components/activities/ArticleAddedToShipmentActivity';
import { ArticleAnalysisCompletedActivity } from '@/components/activities/ArticleAnalysisCompletedActivity';
import { ArticleClientCommentUpdatedActivity } from '@/components/activities/ArticleClientCommentUpdatedActivity';
import { ArticleCreationAcceptedActivity } from '@/components/activities/ArticleCreationAcceptedActivity';
import { ArticleCustomsInformationsProvidedActivity } from '@/components/activities/ArticleCustomsInformationsProvidedActivity';
import { ArticleDispatchProposalRefusedActivity } from '@/components/activities/ArticleDispatchProposalRefusedActivity';
import { ArticleDispatchProposalSubmittedActivity } from '@/components/activities/ArticleDispatchProposalSubmittedActivity';
import { ArticleDispatchProposalValidatedActivity } from '@/components/activities/ArticleDispatchProposalValidatedActivity';
import { ArticleExceptionalBrandCoverageUpdatedActivity } from '@/components/activities/ArticleExceptionalBrandCoverageUpdatedActivity';
import { ArticleExchangeCancelledActivity } from '@/components/activities/ArticleExchangeCancelledActivity';
import { ArticleExchangeConfirmedActivity } from '@/components/activities/ArticleExchangeConfirmedActivity';
import { ArticleExchangeProposedActivity } from '@/components/activities/ArticleExchangeProposedActivity';
import { ArticleExchangeStartedActivity } from '@/components/activities/ArticleExchangeStartedActivity';
import { ArticleExchangeStockAvailabilityConfirmedActivity } from '@/components/activities/ArticleExchangeStockAvailabilityConfirmedActivity';
import { ArticleExchangeStockAvailabilityRefusedActivity } from '@/components/activities/ArticleExchangeStockAvailabilityRefusedActivity';
import { ArticleFeedbackSubmittedActivity } from '@/components/activities/ArticleFeedbackSubmittedActivity';
import { ArticleManualExchangeDueDatesUpdatedActivity } from '@/components/activities/ArticleManualExchangeDueDatesUpdatedActivity';
import { ArticleManuallyArchivedActivity } from '@/components/activities/ArticleManuallyArchivedActivity';
import { ArticleManualRepairDueDatesUpdatedActivity } from '@/components/activities/ArticleManualRepairDueDatesUpdatedActivity';
import { ArticleMediumAddedActivity } from '@/components/activities/ArticleMediumAddedActivity';
import { ArticleMediumDeletedActivity } from '@/components/activities/ArticleMediumDeletedActivity';
import { ArticlePaymentChoiceActivity } from '@/components/activities/ArticlePaymentChoiceActivity';
import { ArticleProcessingCompletedActivity } from '@/components/activities/ArticleProcessingCompletedActivity';
import { ArticleProcessingServiceAdjustedActivity } from '@/components/activities/ArticleProcessingServiceAdjustedActivity';
import { ArticlePropertyUpdatedActivity } from '@/components/activities/ArticlePropertyUpdatedActivity';
import { ArticleQualityControlCompletedActivity } from '@/components/activities/ArticleQualityControlCompletedActivity';
import { ArticleQualityControlServiceAdjustedActivity } from '@/components/activities/ArticleQualityControlServiceAdjustedActivity';
import { ArticleRecyclingDestinationReachedActivity } from '@/components/activities/ArticleRecyclingDestinationReachedActivity';
import { ArticleRemovedFromShipmentActivity } from '@/components/activities/ArticleRemovedFromShipmentActivity';
import { ArticleRepairCompletedActivity } from '@/components/activities/ArticleRepairCompletedActivity';
import { ArticleRepairIssueReportedActivity } from '@/components/activities/ArticleRepairIssueReportedActivity';
import { ArticleRepairStartedActivity } from '@/components/activities/ArticleRepairStartedActivity';
import { ArticleRequalificationAutoValidatedActivity } from '@/components/activities/ArticleRequalificationAutoValidatedActivity';
import { ArticleRequalificationRefusedActivity } from '@/components/activities/ArticleRequalificationRefusedActivity';
import { ArticleRequalificationValidatedActivity } from '@/components/activities/ArticleRequalificationValidatedActivity';
import { ArticleRequalifiedActivity } from '@/components/activities/ArticleRequalifiedActivity';
import { ArticleSelfHandledTransitCompletedActivity } from '@/components/activities/ArticleSelfHandledTransitCompletedActivity';
import { ArticleSelfHandledTransitStartedActivity } from '@/components/activities/ArticleSelfHandledTransitStartedActivity';
import { ArticleSentToZendeskActivity } from '@/components/activities/ArticleSentToZendeskActivity';
import { ArticleServiceChoiceCompletedActivity } from '@/components/activities/ArticleServiceChoiceCompletedActivity';
import { ArticleStatusDueAtUpdatedActivity } from '@/components/activities/ArticleStatusDueAtUpdatedActivity';
import { ArticleSubLocationClearedActivity } from '@/components/activities/ArticleSubLocationClearedActivity';
import { ArticleSubLocationSetActivity } from '@/components/activities/ArticleSubLocationSetActivity';
import { ArticleTransitValidatedActivity } from '@/components/activities/ArticleTransitValidatedActivity';
import { ArticleValidationAutoAcceptedActivity } from '@/components/activities/ArticleValidationAutoAcceptedActivity';
import { ArticleValidationChoiceActivity } from '@/components/activities/ArticleValidationChoiceActivity';
import { ArticleWarrantyForcedActivity } from '@/components/activities/ArticleWarrantyForcedActivity';
import { ArticleWorkshopUpdatedActivity } from '@/components/activities/ArticleWorkshopUpdatedActivity';
import { ClientContactRecordedActivity } from '@/components/activities/ClientContactRecordedActivity';
import { ClientPickupCompletedActivity } from '@/components/activities/ClientPickupCompletedActivity';
import { CommentAddedActivity } from '@/components/activities/CommentAddedActivity';
import { FeedbackSubmittedActivity } from '@/components/activities/FeedbackSubmittedActivity';
import { JobAcceptedActivity } from '@/components/activities/JobAcceptedActivity';
import { JobAutoAcceptedActivity } from '@/components/activities/JobAutoAcceptedActivity';
import { JobRefusedActivity } from '@/components/activities/JobRefusedActivity';
import { JobSentForAcceptationActivity } from '@/components/activities/JobSentForAcceptationActivity';
import { PaymentCompletedActivity } from '@/components/activities/PaymentCompletedActivity';
import { PendingBillPaymentNotifiedActivity } from '@/components/activities/PendingBillPaymentNotifiedActivity';
import { PendingPickupNotifiedActivity } from '@/components/activities/PendingPickupNotifiedActivity';
import { PendingValidationNotifiedActivity } from '@/components/activities/PendingValidationNotifiedActivity';
import { RequestAutomaticallyArchivedActivity } from '@/components/activities/RequestAutomaticallyArchivedActivity';
import { RequestClaimedActivity } from '@/components/activities/RequestClaimedActivity';
import { RequestCompletedActivity } from '@/components/activities/RequestCompletedActivity';
import { RequestCreatedActivity } from '@/components/activities/RequestCreatedActivity';
import { RequestManuallyArchivedActivity } from '@/components/activities/RequestManuallyArchivedActivity';
import { RequestMediumAddedActivity } from '@/components/activities/RequestMediumAddedActivity';
import { RequestMediumDeletedActivity } from '@/components/activities/RequestMediumDeletedActivity';
import { RequestPropertyUpdatedActivity } from '@/components/activities/RequestPropertyUpdatedActivity';
import { RequestSentToZendeskActivity } from '@/components/activities/RequestSentToZendeskActivity';
import { ShipmentArticleVerifiedActivity } from '@/components/activities/ShipmentArticleVerifiedActivity';
import { ShipmentCreatedActivity } from '@/components/activities/ShipmentCreatedActivity';
import { ShipmentFinalisationCompletedActivity } from '@/components/activities/ShipmentFinalisationCompletedActivity';
import { ShipmentHandoverCompletedActivity } from '@/components/activities/ShipmentHandoverCompletedActivity';
import { ShipmentIssueReportedActivity } from '@/components/activities/ShipmentIssueReportedActivity';
import { ShipmentIssueResolvedActivity } from '@/components/activities/ShipmentIssueResolvedActivity';
import { ShipmentPreparationCompletedActivity } from '@/components/activities/ShipmentPreparationCompletedActivity';
import { ShipmentTransitCompletedActivity } from '@/components/activities/ShipmentTransitCompletedActivity';
import { ShipmentVerificationCompletedActivity } from '@/components/activities/ShipmentVerificationCompletedActivity';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import { ErrorBoundary } from '@/services/sentry';
import { createBEMClasses } from '@/utils/classname';
import { formatDate, fromNow } from '@/utils/date';
import { useLoadMore } from '@/utils/useLoadMore';
import useViewPort from '@/utils/useViewport';

import { RequestHandlingFeeWaivedActivity } from '../RequestHandlingFeeWaivedActivity';

import './Activities.css';

const { block, element } = createBEMClasses('activities');

export const Activities = ({
  activities,
  isLoading,
  isError,
  isShipment,
  isArticle,
  nbOfArticles,
  loadNextPage,
}: {
  activities: Activity[];
  isLoading?: boolean;
  isError?: boolean;
  isShipment?: boolean;
  isArticle?: boolean;
  nbOfArticles?: number;
  loadNextPage?: () => void;
}) => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();
  const loadNextPageTriggerRef = useLoadMore(loadNextPage);

  if (isError) {
    return (
      <Paragraph color="disabled" style={isMobile ? undefined : { margin: '16px' }}>
        <Trans id="activities.error">An error occurred when loading activities</Trans>
      </Paragraph>
    );
  }

  if (isLoading) {
    return (
      <Paragraph color="disabled" style={isMobile ? undefined : { margin: '16px' }}>
        <Trans id="activities.loading">Loading</Trans>
      </Paragraph>
    );
  }

  if (activities.length === 0) {
    return (
      <Paragraph color="disabled" style={isMobile ? undefined : { margin: '16px' }}>
        <Trans id="activities.no-activities">No activities yet</Trans>
      </Paragraph>
    );
  }

  const activitiesGroupedByDate = activities.reduce(
    (acc, activity) => {
      const date = new Date(activity.createdAt).toDateString();

      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(activity);

      return acc;
    },
    {} as Record<string, Activity[]>
  );

  return (
    <SimpleBar
      style={{
        padding: isMobile ? undefined : '0 1rem 1.5rem 1rem',
        maxHeight: isMobile ? undefined : '30rem',
      }}
      autoHide={false}
    >
      <Stack className={block()}>
        {Object.entries(activitiesGroupedByDate).map(([activityDate, dailyActivities]) => {
          const date = new Date(activityDate);
          const date9Am = setHours(date, 9);

          let dateLabel = fromNow(date9Am);

          if (isToday(date9Am)) {
            dateLabel = t({ id: 'activities.today', message: 'Today' });
          }
          // if date is more than 3 days ago just display the date
          else if (isBefore(date9Am, new Date(Date.now() - 3 * 24 * 60 * 60 * 1000))) {
            dateLabel = formatDate(date9Am, {
              dateStyle: 'medium',
            });
          }

          return (
            <Stack key={activityDate} className={element('section')}>
              <div className={element('section-header')}>
                <Paragraph weight="medium">{dateLabel}</Paragraph>
              </div>
              <div>
                {stackSimilarActivities(dailyActivities).map((activity) => (
                  <ErrorBoundary key={activity[0].id}>
                    <ActivityComponent
                      activities={activity}
                      isShipment={!!isShipment}
                      isArticle={!!isArticle}
                      nbOfArticles={nbOfArticles}
                    />
                  </ErrorBoundary>
                ))}
              </div>
            </Stack>
          );
        })}
        <div className={element('load_next_page_trigger')} ref={loadNextPageTriggerRef}></div>
      </Stack>
    </SimpleBar>
  );
};

const ActivityComponent = ({
  activities,
  isShipment,
  isArticle,
  nbOfArticles,
}: {
  activities: Activity[];
  isShipment: boolean;
  isArticle: boolean;
  nbOfArticles?: number;
}) => {
  const displayArticleName = !isArticle && !!nbOfArticles && nbOfArticles > 1;
  const activity = activities[0];

  switch (activity.type) {
    case 'article_added_to_shipment':
      return (
        <ArticleAddedToShipmentActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_analysis_completed':
      return (
        <ArticleAnalysisCompletedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_client_comment_updated':
      return (
        <ArticleClientCommentUpdatedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_creation_accepted':
      return (
        <ArticleCreationAcceptedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_dispatch_proposal_refused':
      return (
        <ArticleDispatchProposalRefusedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_dispatch_proposal_submitted':
      return (
        <ArticleDispatchProposalSubmittedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_dispatch_proposal_validated':
      return (
        <ArticleDispatchProposalValidatedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_exchange_cancelled':
      return (
        <ArticleExchangeCancelledActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_exchange_confirmed':
      return <ArticleExchangeConfirmedActivity activity={activity} />;

    case 'article_exchange_proposed':
      return <ArticleExchangeProposedActivity activity={activity} />;

    case 'article_exchange_stock_availability_confirmed':
      return <ArticleExchangeStockAvailabilityConfirmedActivity activity={activity} />;

    case 'article_exchange_stock_availability_refused':
      return <ArticleExchangeStockAvailabilityRefusedActivity activity={activity} />;

    case 'article_exceptional_brand_coverage_updated':
      return <ArticleExceptionalBrandCoverageUpdatedActivity activity={activity} />;

    case 'article_exchange_started':
      return (
        <ArticleExchangeStartedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_feedback_submitted':
      return <ArticleFeedbackSubmittedActivity activity={activity} />;

    case 'article_manual_repair_due_days_updated':
      return (
        <ArticleManualRepairDueDatesUpdatedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_manual_exchange_due_days_updated':
      return (
        <ArticleManualExchangeDueDatesUpdatedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_manually_archived':
      return (
        <ArticleManuallyArchivedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_medium_added':
      return (
        <ArticleMediumAddedActivity activity={activity} displayArticleName={displayArticleName} />
      );

    case 'article_medium_deleted':
      return (
        <ArticleMediumDeletedActivity activity={activity} displayArticleName={displayArticleName} />
      );

    case 'article_payment_choice':
      return (
        <ArticlePaymentChoiceActivity
          activities={activities as ActivityOfType<'article_payment_choice'>[]}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_processing_completed':
      return (
        <ArticleProcessingCompletedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_processing_service_adjusted':
      return (
        <ArticleProcessingServiceAdjustedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_property_updated':
      return (
        <ArticlePropertyUpdatedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_quality_control_completed':
      return (
        <ArticleQualityControlCompletedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_quality_control_service_adjusted':
      return (
        <ArticleQualityControlServiceAdjustedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_customs_informations_provided':
      return <ArticleCustomsInformationsProvidedActivity activity={activity} />;

    case 'article_repair_completed':
      return (
        <ArticleRepairCompletedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_repair_issue_reported':
      return (
        <ArticleRepairIssueReportedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_repair_started':
      return (
        <ArticleRepairStartedActivity activity={activity} displayArticleName={displayArticleName} />
      );

    case 'article_removed_from_shipment':
      return (
        <ArticleRemovedFromShipmentActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_requalification_auto_validated':
      return (
        <ArticleRequalificationAutoValidatedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_requalification_refused':
      return (
        <ArticleRequalificationRefusedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_requalification_validated':
      return (
        <ArticleRequalificationValidatedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_recycling_destination_reached':
      return (
        <ArticleRecyclingDestinationReachedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_requalified':
      return (
        <ArticleRequalifiedActivity activity={activity} displayArticleName={displayArticleName} />
      );

    case 'article_sent_to_zendesk':
      return (
        <ArticleSentToZendeskActivity activity={activity} displayArticleName={displayArticleName} />
      );

    case 'article_service_choice_completed':
      return (
        <ArticleServiceChoiceCompletedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_status_due_at_manually_updated':
      return (
        <ArticleStatusDueAtUpdatedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_self_handled_transit_completed':
      return (
        <ArticleSelfHandledTransitCompletedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_self_handled_transit_started':
      return (
        <ArticleSelfHandledTransitStartedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_transit_validated':
      return (
        <ArticleTransitValidatedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_validation_auto_accepted':
      return (
        <ArticleValidationAutoAcceptedActivity
          activities={activities as ActivityOfType<'article_validation_auto_accepted'>[]}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_validation_choice':
      return (
        <ArticleValidationChoiceActivity
          activities={activities as ActivityOfType<'article_validation_choice'>[]}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_warranty_forced':
      return (
        <ArticleWarrantyForcedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'article_sub_location_set':
      return <ArticleSubLocationSetActivity activity={activity} />;

    case 'article_sub_location_cleared':
      return <ArticleSubLocationClearedActivity activity={activity} />;

    case 'article_workshop_updated':
      return (
        <ArticleWorkshopUpdatedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'client_contact_recorded':
      return (
        <ClientContactRecordedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'client_pickup_completed':
      return (
        <ClientPickupCompletedActivity
          activity={activity}
          nbOfArticles={nbOfArticles ?? 0}
          displayArticleName={displayArticleName}
        />
      );

    case 'comment_added':
      return (
        <CommentAddedActivity activity={activity} isShipment={isShipment} isArticle={isArticle} />
      );

    case 'feedback_submitted':
      return <FeedbackSubmittedActivity activity={activity} />;

    case 'job_accepted':
      return <JobAcceptedActivity activities={activities as ActivityOfType<'job_accepted'>[]} />;

    case 'job_auto_accepted':
      return (
        <JobAutoAcceptedActivity activities={activities as ActivityOfType<'job_auto_accepted'>[]} />
      );

    case 'job_sent_for_acceptation':
      return (
        <JobSentForAcceptationActivity
          activities={activities as ActivityOfType<'job_sent_for_acceptation'>[]}
        />
      );

    case 'job_refused':
      return <JobRefusedActivity activity={activity} displayArticleName={displayArticleName} />;

    case 'payment_completed':
      return <PaymentCompletedActivity activity={activity} />;

    case 'pending_bill_payment_notified':
      return <PendingBillPaymentNotifiedActivity activity={activity} />;

    case 'pending_pickup_notified':
      return <PendingPickupNotifiedActivity activity={activity} />;

    case 'pending_validation_notified':
      return <PendingValidationNotifiedActivity activity={activity} />;

    case 'request_automatically_archived':
      return <RequestAutomaticallyArchivedActivity activity={activity} />;

    case 'request_claimed':
      return <RequestClaimedActivity activity={activity} />;

    case 'request_completed':
      return <RequestCompletedActivity activity={activity} />;

    case 'request_created':
      return <RequestCreatedActivity activity={activity} />;

    case 'request_manually_archived':
      return <RequestManuallyArchivedActivity activity={activity} />;

    case 'request_medium_added':
      return <RequestMediumAddedActivity activity={activity} />;

    case 'request_medium_deleted':
      return <RequestMediumDeletedActivity activity={activity} />;

    case 'request_property_updated':
      return <RequestPropertyUpdatedActivity activity={activity} />;

    case 'request_sent_to_zendesk':
      return <RequestSentToZendeskActivity activity={activity} />;

    case 'shipment_article_verified':
      return (
        <ShipmentArticleVerifiedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'shipment_created':
      return <ShipmentCreatedActivity activity={activity} isShipment={isShipment} />;

    case 'shipment_finalisation_completed':
      return <ShipmentFinalisationCompletedActivity activity={activity} />;

    case 'shipment_handover_completed':
      return <ShipmentHandoverCompletedActivity activity={activity} />;

    case 'shipment_preparation_completed':
      return <ShipmentPreparationCompletedActivity activity={activity} />;

    case 'shipment_issue_reported':
      return (
        <ShipmentIssueReportedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'shipment_issue_resolved':
      return (
        <ShipmentIssueResolvedActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'shipment_transit_completed':
      return <ShipmentTransitCompletedActivity activity={activity} />;

    case 'shipment_verification_completed':
      return <ShipmentVerificationCompletedActivity activity={activity} />;

    case 'article_action_refused_by_client':
      return (
        <ArticleActionRefusedByClientActivity
          activity={activity}
          displayArticleName={displayArticleName}
        />
      );

    case 'request_handling_fee_waived':
      return <RequestHandlingFeeWaivedActivity activity={activity} />;

    default:
      // Exhaustive check to ensure all activity types are handled
      // eslint-disable-next-line no-case-declarations
      const _exhaustiveCheck: never = activity;

      return _exhaustiveCheck && null;
  }
};
